import { splitTextWithConjunctions } from '@/utils/common';

export interface FilterState {
    programId: string | null;
    assessmentRunId: string | null;
}

export interface ML360Statistic {
    label: string;
    shortLabel: string;
    value: string;
    total: number;
}

export interface SelfDeterminationStatistic {
    domainMax: number;
    radarData: { shortLabel: string; value: number }[];
    summaryCards: { label: string; value: number; total: number }[];
}

export interface TeamResilienceStatistic {
    domainMax: number;
    radarData: { shortLabel: string; value: number }[];
    summaryCards: { label: string; value: number; total: number }[];
    tickCount: number;
}

export interface SpatialPlanningResultStatistic {
    correctAnswers: number;
    incorrectAnswers: number;
    accuracy: number;
    averageResponseTimeMs: number;
    fastestResponseTimeMs: number;
}

export interface SartTrialChartData {
    name: string;
    data: { index: number; responseTimeMs: number }[];
}

export interface SartStatistics {
    series: SartTrialChartData[];
    line: SartTrialChartData;
    accuracy: number;
    averageResponseTimeMs: number;
    fastestResponseTimeMs: number;
}

export interface VisualReactionTrialChartData {
    question: number;
    responseTimeMs: number;
    isCorrect: boolean;
    isMissedResponse: boolean;
}

export interface VisualReactionRoundChartData {
    trials: VisualReactionTrialChartData[];
    averageResponseTimeMs: number;
    accuracy: number;
    roundIndex: number;
    fastestTimeMs: number;
}

export interface VisualReactionStatistics {
    rounds: VisualReactionRoundChartData[];
    accuracy: number;
    averageResponseTimeMs: number;
    fastestResponseTimeMs: number;
}

export const convertShortLabel = (label: string): string => {
    switch (label) {
        case 'Demonstrate Self and Situational Awareness':
            return 'Awareness';
        case 'Exude Calm and Patience':
            return 'Calm & Patience';
        case 'Adopt an Open Mind':
            return 'Open Mind';
        case 'Maintain Focus and Presence of Mind':
            return 'Focus & Presence';
        case 'Seek New Ideas and Learning':
            return 'Growth';
        default:
            return label;
    }
};

// Create a reusable tick renderer for PolarAngleAxis labels that adds
// an outward radial offset to improve spacing between the radar and labels.
export const createRadialLabelTickRenderer = (
    offsetPx: number,
    options?: { fontSize?: number; fill?: string; fontWeight?: number | string; wrapWords?: boolean }
) => {
    const { fontSize = 12, fill = 'var(--color-neutral-500)', fontWeight = 500, wrapWords = false } = options || {};
    const RadialLabelTickRenderer = (props: any) => {
        const { x, y, cx, cy, payload, textAnchor } = props;
        const dx = x - cx;
        const dy = y - cy;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        const nx = x + (dx / distance) * offsetPx;
        const ny = y + (dy / distance) * offsetPx;

        if (wrapWords && payload.value) {
            const text = String(payload.value);
            const lines = splitTextWithConjunctions(text);

            const lineHeight = fontSize * 1.5;
            const totalHeight = lines.length * lineHeight;
            const startY = ny - (totalHeight - lineHeight) / 2;

            return (
                <text x={nx} y={startY} fill={fill} fontSize={fontSize} fontWeight={fontWeight} textAnchor={textAnchor}>
                    {lines.map((line: string, index: number) => (
                        <tspan key={index} x={nx} dy={index === 0 ? 0 : lineHeight}>
                            {line}
                        </tspan>
                    ))}
                </text>
            );
        }

        return (
            <text x={nx} y={ny} fill={fill} fontSize={fontSize} fontWeight={fontWeight} textAnchor={textAnchor}>
                {payload.value}
            </text>
        );
    };

    return RadialLabelTickRenderer;
};
